"use client";

import { PlatformError, isPlatformErrorBody, kindForStatus } from "./client";
import { clampLimit } from "./paginate";

/* Client-side access to the public API.

   Never call the API host directly from the browser: it sends no
   access-control-allow-origin for our origins, so the request is refused.
   Everything here goes through our own /api/platform proxy, which is
   same-origin and attaches the visitor's IP on the way out. */

export const PLATFORM_PROXY = "/api/platform";

const PROXY_TIMEOUT_MS = 12_000;

/* Where a read is addressed, and whether it carries cookies.

   On a demo inside the API's own domain the layout stamps the resolved API base
   on <html data-platform-api>; there the session cookie lives on the API host,
   so a read goes straight there with `credentials: "include"` — the proxy could
   not see that cookie to forward it. Everywhere else (a Vercel preview, local
   dev) there is no stamp, and reads keep to the same-origin proxy, which
   rewrites the cookie onto the app's own origin.

   Reads only. Writes stay on the proxy regardless: it presents the site apex as
   the sign-in Origin (a demo's own origin is not on the platform's allowlist),
   which a direct call — whose Origin the browser fixes to the real host — cannot
   do. See direct.ts and the proxy's APEX_ORIGIN_PATHS. */
const directBase = (): string | null => {
  if (typeof document === "undefined") {
    return null;
  }

  const base = document.documentElement.dataset.platformApi?.trim();

  return base ? base.replace(/\/+$/, "") : null;
};

/* The API base the browser may address itself, or null behind the proxy. The
   Google and X popups open on it (lib/api/social.ts). */
export const directPlatformBase = directBase;

const readTarget = (path: string, search: string) => {
  const base = directBase();

  return base
    ? { url: `${base}/${path}${search}`, credentials: "include" as const }
    : { url: `${PLATFORM_PROXY}/${path}${search}`, credentials: "same-origin" as const };
};

/* Writes go direct on the same terms as reads, with one exception.

   Every write that needs the session — a reward claim, a deposit, a
   withdrawal, a profile edit, a sign-out — needs the cookie that lives on the
   API host, and only a direct credentialed call carries it. Proxied, the
   browser sends no API-host cookie to the demo host, the proxy forwards none,
   and the write answers 401: that is how a signed-in player was told to
   "sign in to make changes" to rewards they could plainly see. Sign-out in
   particular must go direct, or it clears a cookie the demo never held.

   The exception is the sign-in paths the platform gates on Origin against
   site.hostnames. A demo's own origin is not on that list; the proxy presents
   the site apex in its place (signin-origin.ts), which a direct call cannot —
   the browser fixes Origin to the real host. Those stay on the proxy.

   Direct writes skip the proxy's same-origin check and write throttle. The
   API's credentialed CORS — which echoes only allowlisted origins — is the
   browser-side guard, and it is how the whitelabel has always written. */
/* Sign-in goes direct too, the way the whitelabel's SDK does it: the API sets
   the session and device cookies itself, for the domain the site's config
   names, and the browser stores them for every host beneath it. Routed
   through the proxy instead, those Set-Cookie headers had to be relayed and
   rewritten, and a magic link that verified fine still left the player
   signed out. Sign-in used to be the one write kept on the proxy so it could
   present the site apex as Origin — an idea that mailed players links to the
   wrong site and is gone (see the proxy). What the proxy still offers a
   sign-in call is a plain refusal for a host the platform does not trust:
   a direct call from such a host dies in CORS with nothing to show, so that
   one failure falls back to the proxy for its message. */
const SIGNIN_PATHS = [/^signin\//];

const proxyTarget = (path: string) => ({
  url: `${PLATFORM_PROXY}/${path}`,
  credentials: "same-origin" as const,
});

const writeTarget = (path: string) => {
  const base = directBase();

  if (base) {
    return { url: `${base}/${path}`, credentials: "include" as const };
  }

  return proxyTarget(path);
};

/* The platform scopes its session cookie by `xx-forwarded-host`: the first
   host in it is matched against the site's `cookieDomains`, and a match sets
   the cookie for that domain (`.wagmicasino.com`) instead of the API host
   alone. The site itself is still resolved from the last host, which the
   API's edge appends, so this cannot change which site answers. Without it a
   demo's session lives on the API host only, the demo's own server never sees
   it, and chat — which posts as the player from that server — treats every
   signed-in player as a guest. A custom header costs a CORS preflight, so it
   rides on the writes (preflighted anyway) and on `whoami`, which every page
   calls and which re-issues an existing cookie under the new domain. */
const forwardedHost = (direct: boolean): Record<string, string> =>
  direct && typeof location !== "undefined" ? { "xx-forwarded-host": location.host } : {};

const SESSION_READ_PATHS = new Set(["whoami"]);

type Query = Record<string, string | number | string[] | undefined>;

const withTimeout = (input: string, init?: RequestInit) => {
  const timeout = AbortSignal.timeout(PROXY_TIMEOUT_MS);

  return fetch(input, { ...init, signal: timeout }).catch((cause) => {
    if (timeout.aborted) {
      throw new PlatformError(504, "timeout", `The API did not answer within ${PROXY_TIMEOUT_MS}ms.`, cause);
    }

    throw new PlatformError(0, "network", "Could not reach the API.", cause);
  });
};

const toSearchParams = (query: Query) => {
  const params = new URLSearchParams();

  for (const [key, value] of Object.entries(query)) {
    if (value === undefined) {
      continue;
    }

    /* Array parameters repeat the key — the API rejects a comma-joined list
       with "expected array". */
    if (Array.isArray(value)) {
      for (const entry of value) {
        params.append(key, entry);
      }

      continue;
    }

    params.set(key, String(value));
  }

  return params;
};

export const platformFetch = async <T>(path: string, query: Query = {}) => {
  const params = toSearchParams(query);
  const search = params.size > 0 ? `?${params}` : "";
  const target = readTarget(path, search);

  const direct = target.credentials === "include";
  const response = await withTimeout(target.url, {
    credentials: target.credentials,
    headers: SESSION_READ_PATHS.has(path) ? forwardedHost(direct) : {},
  });

  const body = await response.json().catch(() => null);

  if (!response.ok) {
    const kind = kindForStatus(response.status);

    if (isPlatformErrorBody(body)) {
      throw new PlatformError(response.status, kind, body.error.message, body.error.details);
    }

    throw new PlatformError(response.status, kind, `Request failed with ${response.status}.`, body);
  }

  return body as T;
};

/* Writes. Same proxy, same error contract; `credentials` matters here because
   sign-in and sign-out are the calls that set and clear the session cookie. */
export const platformSend = async <T>(
  path: string,
  body: unknown,
  method: "POST" | "PATCH" | "DELETE" = "POST",
) => {
  const target = writeTarget(path);
  const init = {
    method,
    headers: { "content-type": "application/json" },
    body: JSON.stringify(body),
  };

  let response: Response;
  try {
    response = await withTimeout(target.url, {
      ...init,
      headers: { ...init.headers, ...forwardedHost(target.credentials === "include") },
      credentials: target.credentials,
    });
  } catch (error) {
    /* A direct sign-in call from a host the platform does not trust never
       gets a response — the browser blocks it in CORS. The proxy answers
       the same request with a 403 that names the missing hostnames entry. */
    const untrustedSignIn =
      target.credentials === "include" &&
      SIGNIN_PATHS.some((pattern) => pattern.test(path)) &&
      error instanceof PlatformError &&
      error.kind === "network";
    if (!untrustedSignIn) throw error;
    const fallback = proxyTarget(path);
    response = await withTimeout(fallback.url, { ...init, credentials: fallback.credentials });
  }

  const parsed = await response.json().catch(() => null);

  if (!response.ok) {
    const kind = kindForStatus(response.status);

    if (isPlatformErrorBody(parsed)) {
      throw new PlatformError(response.status, kind, parsed.error.message, parsed.error.details);
    }

    throw new PlatformError(response.status, kind, `Request failed with ${response.status}.`, parsed);
  }

  return parsed as T;
};
